import { InboxOutlined } from "@ant-design/icons";
import { Progress, Upload } from "antd";
import axios from "axios";
import _ from "lodash";
import React, { useRef, useState } from "react";
import styled from "styled-components";

import { Button } from "@components/Button";
import { Text } from "@components/Text";
import { COLOR_GREEN, COLOR_GREY } from "@static/COLORS";
import { formatBytes } from "@utils/formatBytes";
import { generateDragMultipleUpload } from "@utils/generateDragMultipleUpload";
import { configHeader } from "@utils/https/configHeader";

import { MemoizedReactPlayer } from "./MemoizedReactPlayer";

const { Dragger } = Upload;

const DeviceContainer = styled.div`
  width: 1920px;
  height: 1080px;
  background: white;
  padding: 20px;
  overflow: scroll;
`;

const ContainerDrag = styled.div`
  width: 400px;
  height: 400px;
`;

const ContainerPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContainerMediaPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContainerMediaItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const MediaItem = styled.div`
  width: 170px;
  height: 120px;
  overflow: hidden;
  border: 1px dashed ${COLOR_GREY};
  border-radius: 8px;
  flex-shrink: 0;
`;

const ContainerAction = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const MediaContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
`;

const customer_id = 1;
const location_id = 3;
const { REACT_APP_API_ENDPOINT, REACT_APP_REQUEST_TIME_OUT } = process.env;

const MultipleFileUpload = () => {
  const canvasRef = useRef(null);
  const playerRefs = useRef([]);
  const [fileList, setFileList] = useState([]);
  const [progress, setProgress] = useState({});

  const base64ToFile = ({ base64, fileName, mimeType = "image/png" }) => {
    const byteString = atob(base64.split(",")[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      intArray[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([intArray], { type: mimeType });
    const file = new File([blob], fileName, {
      type: mimeType,
      lastModified: Date.now(),
    });

    return file;
  };

  const captureThumbnail = ({ index }) => {
    const player = playerRefs.current[index]?.getInternalPlayer();
    const canvas = canvasRef?.current;
    const ctx = canvas.getContext("2d");
    const width = 640;
    const height = 360;
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(player, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL();
    return dataUrl;
  };

  const onHandleConfirm = async () => {
    for (let index = 0; index < fileList.length; index++) {
      const item = fileList[index];
      const fileKey = _.get(item, ["fileKey"]);

      const isVideo = _.chain(item)
        .get(["type"])
        .split("/")
        .first()
        .isEqual("video")
        .value();

      const thumbnailBase64 = isVideo ? captureThumbnail({ index }) : null;
      const thumbnailFile = thumbnailBase64
        ? base64ToFile({
            base64: thumbnailBase64,
            fileName: `${fileKey}-thumbnail.png`,
          })
        : null;

      let isUploadComplete = false;

      try {
        const resp = await axios({
          method: "post",
          url: `${REACT_APP_API_ENDPOINT}${"/api/v1/medias-upload-file"}`,
          data: {
            category_customer_id: _.get(item, ["category_customer_id"]),
            location_id: _.get(item, ["location_id"]),
            display_name: _.get(item, ["display_name"]),
            extension: _.get(item, ["extension"]),
            type: _.get(item, ["type"]),
            duration: _.get(item, ["duration"]),
            size: _.get(item, ["size"]),
            file: _.get(item, ["file"]),
            thumbnailFile,
            fileKey,
            is_share: true,
          },

          headers: configHeader({ auth: true }),
          timeout: REACT_APP_REQUEST_TIME_OUT,
          onUploadProgress: (progressEvent) => {
            // Only update progress if upload is not complete
            if (!isUploadComplete) {
              const percentCompleted = Math.round(
                (progressEvent?.loaded * 100) / progressEvent?.total
              );
              console.log(`percentCompleted :>> ${fileKey}`, percentCompleted);
              setProgress((prev) => {
                return { ...prev, [fileKey]: percentCompleted };
              });
            }
          },
        });
        isUploadComplete = true;
        setProgress((prev) => {
          return { ...prev, [fileKey]: 100 };
        });

        console.log("fileKey :>> ", fileKey);
        console.log(`File ${index + 1} upload complete:`, resp);
      } catch (error) {
        console.error(`Error uploading file ${index + 1}:`, error);
      }
    }
    console.log("All files uploaded successfully");
  };

  const onHandleClearFileList = () => {
    setFileList([]);
    setProgress({});
    playerRefs.current = [];
  };

  return (
    <DeviceContainer>
      {_.size(fileList) === 0 && (
        <ContainerDrag>
          <Dragger
            {...generateDragMultipleUpload({
              customer_id,
              location_id,
              setFileList,
            })}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for multiple files or folder uploads.
            </p>
          </Dragger>
        </ContainerDrag>
      )}

      {_.size(fileList) > 0 && (
        <ContainerPreview>
          <ContainerAction>
            <Button color={COLOR_GREEN} onClick={() => onHandleClearFileList()}>
              Clear
            </Button>

            <Button color={COLOR_GREEN} onClick={() => onHandleConfirm()}>
              Confirm Uploads
            </Button>
          </ContainerAction>

          <ContainerMediaPreview>
            {_.map(fileList, (item, index) => {
              const type = _.get(item, ["type"]);
              const file = _.get(item, ["file"]);
              const size = _.get(item, ["size"]);
              const duration = _.get(item, ["duration"]);
              const fileKey = _.get(item, ["fileKey"]);
              const isImage = _.startsWith(type, "image/");

              return (
                <ContainerMediaItem key={index}>
                  <MediaItem>
                    {isImage ? (
                      <img
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                        src={URL.createObjectURL(file)}
                        alt={_.get(file, ["name"], "-")}
                      />
                    ) : (
                      <MemoizedReactPlayer
                        file={file}
                        index={index}
                        playerRefs={playerRefs}
                      />
                    )}
                  </MediaItem>
                  <MediaContent>
                    <div>
                      <Text>Name: {_.get(item, ["display_name"])}</Text>
                      <Text>Size: {formatBytes({ bytes: size })}</Text>
                      <Text>Duration: {duration} Sec.</Text>
                    </div>
                    <div>
                      <Progress
                        style={{ width: "97%" }}
                        percent={_.get(progress, [fileKey])}
                        type="line"
                      />
                    </div>
                  </MediaContent>
                </ContainerMediaItem>
              );
            })}
          </ContainerMediaPreview>
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </ContainerPreview>
      )}
    </DeviceContainer>
  );
};

export default MultipleFileUpload;
